import {cn} from '@ui/utils/cn';
import {ReactNode, RefObject, useCallback, useEffect, useRef, useState} from 'react';
import {pickActiveSection, scrollTopFor} from './scroll-spy';

/** Closest scrollable ancestor (falls back to the document). */
export function getScrollParent(el: HTMLElement | null): HTMLElement {
  let node = el?.parentElement ?? null;
  while (node) {
    const {overflowY} = getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return (document.scrollingElement as HTMLElement) ?? document.documentElement;
}

function visibleTop(container: HTMLElement): number {
  return container === document.scrollingElement || container === document.documentElement
    ? 0
    : container.getBoundingClientRect().top;
}

/** Smoothly scroll `id` into view inside its scroll container. */
export function scrollToSection(id: string, offset = 16) {
  const el = document.getElementById(id);
  if (!el) return false;
  const container = getScrollParent(el);
  const top = el.getBoundingClientRect().top - visibleTop(container);
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  container.scrollTo({
    top: scrollTopFor(container.scrollTop, top, offset),
    behavior: reduce ? 'auto' : 'smooth',
  });
  return true;
}

/**
 * Scrollspy: returns the id of the section at the top of the scroll
 * container. `ids` should be the currently rendered (e.g. filtered) sections.
 */
export function useScrollSpy(
  ids: string[],
  {offset = 24, anchorRef}: {offset?: number; anchorRef?: RefObject<HTMLElement | null>} = {},
) {
  const [active, setActive] = useState<string | null>(ids[0] ?? null);
  // Section picked from the nav: stays highlighted until the user scrolls
  // (the last sections may be too short to reach the offset line).
  const lockRef = useRef<string | null>(null);
  const key = ids.join('|');

  useEffect(() => {
    const first = ids.length ? document.getElementById(ids[0]) : null;
    const container = getScrollParent(anchorRef?.current ?? first);
    const target: HTMLElement | Window =
      container === document.scrollingElement ? window : container;
    let frame = 0;
    const measure = () => {
      frame = 0;
      if (lockRef.current && ids.includes(lockRef.current)) {
        setActive(lockRef.current);
        return;
      }
      lockRef.current = null;
      const base = visibleTop(container);
      const sections = ids
        .map(id => {
          const el = document.getElementById(id);
          return el ? {id, top: el.getBoundingClientRect().top - base} : null;
        })
        .filter(Boolean) as {id: string; top: number}[];
      const atBottom =
        container.scrollTop > 0 &&
        container.scrollTop + container.clientHeight >= container.scrollHeight - 8;
      setActive(pickActiveSection(sections, offset, atBottom));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    const release = () => {
      if (lockRef.current) {
        lockRef.current = null;
        onScroll();
      }
    };
    const userEvents = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;
    measure();
    target.addEventListener('scroll', onScroll, {passive: true});
    window.addEventListener('resize', onScroll);
    userEvents.forEach(e => container.addEventListener(e, release, {passive: true}));
    return () => {
      target.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      userEvents.forEach(e => container.removeEventListener(e, release));
      if (frame) cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, offset]);

  const select = useCallback((id: string | null) => {
    lockRef.current = id;
    setActive(id);
  }, []);

  return [active, select] as const;
}

export interface SectionNavItem {
  id: string;
  label: ReactNode;
  meta?: ReactNode;
}

/**
 * Sticky in-page navigation. Uses buttons (never `href="#…"`, which the
 * `<base href="/">` + router turn into a navigation away from the page).
 */
export function SectionNav({
  items,
  offset = 16,
  className,
  testId,
}: {
  items: SectionNavItem[];
  offset?: number;
  className?: string;
  testId?: string;
}) {
  const ref = useRef<HTMLElement>(null);
  const [active, setActive] = useScrollSpy(
    items.map(i => i.id),
    {offset: offset + 8, anchorRef: ref},
  );
  const go = useCallback(
    (id: string) => {
      if (scrollToSection(id, offset)) setActive(id);
    },
    [offset, setActive],
  );
  return (
    <nav ref={ref} className={className} data-testid={testId}>
      <ul className="sticky top-0 space-y-0.5 text-sm">
        {items.map(item => {
          const selected = item.id === active;
          return (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => go(item.id)}
                aria-current={selected ? 'location' : undefined}
                data-section={item.id}
                data-active={selected || undefined}
                className={cn(
                  'flex w-full items-center justify-between gap-2 rounded-md border-s-2 px-2 py-1.5 text-start transition-colors',
                  selected
                    ? 'border-primary bg-primary/5 font-medium text-foreground'
                    : 'border-transparent text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                <span className="truncate">{item.label}</span>
                {item.meta}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
